// src/components/OrdersPreview.jsx
import { Link } from 'react-router-dom'; // Removed useNavigate
import { isValid } from 'date-fns'; // Removed parseISO as date is pre-parsed
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faChevronRight, faArrowsRotate } from '@fortawesome/free-solid-svg-icons';
import { useOrder } from '../contexts/OrderContext';
import { useNavigateWithContext } from '../hooks/useNavigateWithContext';
import { useRefillButtonGate } from '../hooks/useRefillButtonGate';

import { BASE_URL } from '../utilities/api';
import Spinner from './Spinner';
import { getRefillActionState } from '../utilities/refillRules';
import { findProductByCode, formatDispenseQuantity } from '../utilities/productDisplay';

// --- Helper function for border color class ---
const getBorderClass = (productName, dosage) => {
    const nameLower = productName?.toLowerCase() || '';
    const dosageLower = dosage?.toLowerCase() || '';

    // Define border colors based on your theme (e.g., primary/secondary or specific colors)
    // Using example blue/green shades here
    if (nameLower.includes('tirzepatide')) {
        switch (dosageLower) {
            case '2.5mg': return 'border-l-blue-300';
            case '5mg':   return 'border-l-blue-400';
            case '7.5mg': return 'border-l-blue-500';
            case '10mg':  return 'border-l-blue-600';
            case '12.5mg':return 'border-l-blue-700';
            case '15mg':  return 'border-l-blue-800';
            default:      return 'border-l-gray-400'; // Default Tirzepatide border
        }
    } else if (nameLower.includes('semaglutide')) {
        switch (dosageLower) {
            case '0.25mg': return 'border-l-green-300';
            case '0.5mg':  return 'border-l-green-400';
            case '1mg':    return 'border-l-green-500';
            case '1.7mg':  return 'border-l-green-600';
            case '2.4mg':  return 'border-l-green-700';
            default:       return 'border-l-gray-400'; // Default Semaglutide border
        }
    }

    // Fallback for unknown products
    return 'border-l-gray-400';
};
// --- End Helper ---

// Accept props from Dashboard
const OrdersPreview = () => {
  const orderContext = useOrder();
  const navigateWithContext = useNavigateWithContext();
  const { requestRefill, gateModal: refillGateModal } = useRefillButtonGate();

  return (
    <div className="flex flex-col h-full">
      {/* Header */}
      <div className="flex items-center justify-between mb-2">
        <h4 className="text-primary font-semibold">Recent Orders</h4>
        {/* Refresh button uses the passed handler */}
        <button
          className={`text-lg text-gray-400 hover:text-primary-light ${orderContext?.isLoading ? 'animate-spin' : ''}`} // Add spin animation on load
          onClick={orderContext?.refreshOrders}
          title="Refresh Orders"
          disabled={orderContext?.isLoading} // Disable while loading
        >
          <FontAwesomeIcon icon={faArrowsRotate} />
        </button>
      </div>

      {/* Loading State - Use prop */}
      {orderContext?.isLoading && <div className="flex justify-center items-center flex-grow"><Spinner /></div>}

      {/* Error State - Use prop */}
      {!orderContext?.isLoading && orderContext?.error && <p className="text-red-500 text-sm">{orderContext.error}</p>}

      {/* No Orders State - Check orders prop */}
      {!orderContext?.isLoading && !orderContext?.error  && orderContext?.orders.length === 0 && <p className="text-neutral-dark text-sm">No recent orders found.</p>}

      {/* Orders List - Use orders prop */}
      {!orderContext?.isLoading && !orderContext?.error  && orderContext?.orders.length > 0 && (
        <ul className="space-y-3 overflow-y-auto flex-grow rounded shadow p-1">
          {/* Slice the orders prop */}
          {orderContext?.recentOrders.map((order) => {
            // Ensure order.order_date is a valid Date object before calculations
            if (!order.order_date || !isValid(order.order_date)) {
                return null; // Skip rendering this order if date is invalid
            }
            const refillState = getRefillActionState(order, orderContext?.orders);
            const borderClass = getBorderClass(order.product_name, order.dosage);
            const orderProduct = findProductByCode(orderContext?.products, order.product_code);
            const orderQuantity = formatDispenseQuantity(orderProduct, order.order_qty)
              || (order.order_qty ? `${order.order_qty} month${String(order.order_qty) === '1' ? '' : 's'}` : 'Not available');

            return (
              <li
                key={order.order_id}
                className={`border-l-4 ${borderClass} pl-3 py-2 border-b rounded-lg bg-white border-neutral-light last:border-b-0`}
              >
                <div className="flex justify-between items-start gap-2">
                  <div className="w-max">
                    <p className="text-sm font-medium text-neutral-darker">
                      {order.product_name} {order.dosage && `(${order.dosage})`}
                    </p>
                    <p className="mt-2 text-xs text-neutral-dark">
                      Ordered: {order.order_date.toLocaleDateString()} | Quantity: {orderQuantity} | Status: {order.order_status}
                    </p>

                    {/* Conditional Rendering for View Receipt Link */}
                    {refillState.isPaid && (
                      <p className="text-sm mb-0 text-neutral-500 whitespace-nowrap shrink-0 p-0">
                      <Link to={order.receipt_url || `${BASE_URL}/viewreceipt/order/${order.order_id}`} target='_blank' className="text-secondary hover:underline">
                        View Receipt
                      </Link>
                    </p>
                    )}
                  </div>

                  {/* Conditional Rendering for Refill/Days Left */}
                  {refillState.isRefillable ? (
                    <button
                      onClick={() => requestRefill(order.order_id)}
                      className="w-16 bg-button text-white px-2 py-1 mr-2 mt-2 text-sm rounded hover:bg-button-light whitespace-nowrap shrink-0"
                      title="Refill this order"
                    >
                      Refill
                    </button>
                  ) : (
                    // Only show days left if it's the most recent order and days > 0
                    refillState.showRefillCountdown ? (
                      <p className="text-xs text-neutral-500 whitespace-nowrap shrink-0 self-center p-1">
                        {refillState.daysUntilRefill} day{refillState.daysUntilRefill !== 1 ? 's' : ''} left
                      </p>
                    ) : null // Don't show anything if not most recent or days <= 0
                  )}

                  {/* Conditional Rendering for Pay Button */}
                  {refillState.isUnpaid && (
                    <button
                      onClick={() => navigateWithContext(order.order_id, "/payment", "order")}
                      className="w-16 bg-button text-white px-2 py-1 mr-2 mt-2 text-sm rounded hover:bg-button-light whitespace-nowrap shrink-0"
                      title="Pay for this order"
                    >
                      Pay
                    </button>
                  )} 
                </div>
              </li>
            );
          })}
        </ul>
      )}

      {/* View All Link */}
      {!orderContext?.isLoading && (
        <div className="mt-auto pt-2 text-right">
          <Link to="/orders" className="text-sm text-primary hover:underline font-medium">
            View All Orders <FontAwesomeIcon icon={faChevronRight} size="xs" />
          </Link>
        </div>
      )}
      {refillGateModal}
    </div>
  );
};

export default OrdersPreview;
